import React, { useEffect, useState } from 'react';
import ModalLayer from './ModalLayer';
import { VendorTapCard, callControlStyle } from './TapToExpand';
import { formatPhoneDisplay } from '../shared/phoneFormat';
import { ServesLine } from './ServiceAreaFields';
import {
  ADDED_BY_CLIENTS,
  ADDED_BY_CLIENTS_LINE,
  ADDED_BY_YOU,
  ADD_VENDOR,
  ADOPT_BUTTON,
  ADOPT_NOTICE,
  CLIENT_PROS_HEADING,
  EMPTY_ADD_VENDOR,
  EMPTY_LIST_VENDORS,
  IN_YOUR_VENDORS,
  SAVED_SECTION_LINE,
  SAVED_SECTION_TITLE,
  SHARE_NOTICE,
  homeownerTradeOptions,
  proInput,
} from '../shared/homeownerPros';
import { vendorSoftCardStyle } from '../shared/vendorCardStyle';
import SearchableCategorySelect from './SearchableCategorySelect';

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 17.5rem), 1fr))',
  alignItems: 'start',
  gap: '1rem',
  width: '100%',
  minWidth: 0,
};

function tagStyle(colors) {
  return {
    display: 'inline-block',
    margin: '0 0 0.45rem',
    padding: '0.2rem 0.55rem',
    borderRadius: '999px',
    background: colors.primaryLight || 'rgba(89, 196, 196, 0.12)',
    color: colors.text,
    border: 'none',
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: 1.3,
  };
}

function plainLink(event) {
  if (event && event.stopPropagation) event.stopPropagation();
}

function websiteHref(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

export function HomeownerProCard({
  pro,
  colors,
  accent,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  showNotes = false,
  tag = '',
  domId = '',
}) {
  const vendor = { id: pro.id, name: pro.name, category: pro.trade };
  return (
    <div id={domId || undefined} style={vendorSoftCardStyle(colors)}>
      {tag ? <span style={tagStyle(colors)}>{tag}</span> : null}
      <VendorTapCard
        vendor={vendor}
        expanded={expanded}
        onToggle={onToggle}
        accent={accent}
        contact={pro.phone ? (
          <a
            href={`tel:${pro.phone}`}
            aria-label={`Call ${pro.name}`}
            onClick={plainLink}
            style={callControlStyle(accent, colors.border)}
          >
            Call
          </a>
        ) : null}
      >
        <ServesLine record={pro} />
        {pro.phone ? <p style={{ margin: '0 0 0.75rem', color: colors.text }}>{formatPhoneDisplay(pro.phone) || pro.phone}</p> : null}
        {pro.email ? (
          <div style={{ marginBottom: '0.75rem' }}>
            <a href={`mailto:${pro.email}`} onClick={plainLink} style={{ color: colors.textSecondary, fontSize: '0.9rem', textDecoration: 'none', overflowWrap: 'anywhere' }}>
              {pro.email}
            </a>
          </div>
        ) : null}
        {pro.website ? (
          <div style={{ marginBottom: '0.75rem' }}>
            <a
              href={websiteHref(pro.website)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={plainLink}
              style={{ color: colors.textSecondary, fontSize: '0.9rem', textDecoration: 'none', overflowWrap: 'anywhere' }}
            >
              Visit Website
            </a>
          </div>
        ) : null}
        {showNotes && pro.notes ? (
          <p style={{ margin: '0 0 0.75rem', color: colors.textSecondary, overflowWrap: 'anywhere' }}>{pro.notes}</p>
        ) : null}
        {(onEdit || onDelete) ? (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {onEdit ? (
              <button type="button" onClick={() => onEdit(pro)} style={actionStyle(colors, false)}>
                Edit
              </button>
            ) : null}
            {onDelete ? (
              <button type="button" onClick={() => onDelete(pro)} style={actionStyle(colors, true)}>
                Delete
              </button>
            ) : null}
          </div>
        ) : null}
      </VendorTapCard>
    </div>
  );
}

function actionStyle(colors, danger) {
  return {
    background: 'transparent',
    color: danger ? '#b91c1c' : colors.text,
    border: `1px solid ${danger ? '#b91c1c' : colors.border}`,
    borderRadius: '0.5rem',
    padding: '0.55rem 0.8rem',
    minHeight: '44px',
    fontWeight: 650,
    cursor: 'pointer',
  };
}

export function SavedProvidersSection({ pros, colors, accent, onEdit, onDelete, focusId = null }) {
  const [expandedId, setExpandedId] = useState(null);
  useEffect(() => {
    if (focusId) setExpandedId(focusId);
  }, [focusId]);
  const list = Array.isArray(pros) ? pros : [];
  return (
    <section aria-label={SAVED_SECTION_TITLE} style={{ marginBottom: '1.75rem', minWidth: 0 }}>
      <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: colors.text }}>{SAVED_SECTION_TITLE}</h3>
      <p style={{ margin: '0 0 1rem', color: colors.textSecondary, lineHeight: 1.45 }}>{SAVED_SECTION_LINE}</p>
      {list.length === 0 ? (
        <p style={{ margin: 0, color: colors.text }}>{EMPTY_ADD_VENDOR}</p>
      ) : (
        <div style={gridStyle}>
          {list.map((pro) => {
            const expanded = expandedId === pro.id;
            return (
              <HomeownerProCard
                key={pro.id}
                domId={`vendor-${pro.id}`}
                pro={pro}
                colors={colors}
                accent={accent}
                expanded={expanded}
                onToggle={() => setExpandedId(expanded ? null : pro.id)}
                onEdit={onEdit}
                onDelete={onDelete}
                showNotes
                tag={ADDED_BY_YOU}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

export function HomeownerProSheet({ pro, colors, onClose, onSave, customCategories, startOpen = false }) {
  const editing = Boolean(pro && pro.id);
  const [form, setForm] = useState({
    name: (pro && pro.name) || '',
    trade: (pro && pro.trade) || '',
    phone: (pro && pro.phone) || '',
    email: (pro && pro.email) || '',
    website: (pro && pro.website) || '',
    notes: (pro && pro.notes) || '',
  });
  const [error, setError] = useState('');
  const trades = homeownerTradeOptions(customCategories);
  const tradeValue = trades.indexOf(form.trade) === -1 ? '' : form.trade;

  const setField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const submit = (event) => {
    event.preventDefault();
    const parsed = proInput({ ...form, trade: tradeValue || form.trade }, customCategories);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError('');
    onSave(parsed.record);
  };

  const fieldStyle = {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    minHeight: '44px',
    fontSize: '1rem',
    padding: '0.55rem 0.75rem',
    borderRadius: '0.5rem',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
  };

  return (
    <ModalLayer className="app-modal-layer" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0.75rem' }}>
      <div
        className="app-modal-card homeowner-pro-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="homeowner-pro-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          background: colors.surface,
          color: colors.text,
          width: 'min(100%, 32rem)',
          maxWidth: '100%',
          boxSizing: 'border-box',
          borderRadius: '1rem',
          border: `1px solid ${colors.border}`,
        }}
      >
        <div className="app-modal-header" style={{ background: colors.surface, display: 'block' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
            <h2 id="homeowner-pro-title" style={{ margin: 0, fontSize: '1.25rem' }}>
              {editing ? 'Edit vendor' : ADD_VENDOR}
            </h2>
            <button type="button" className="app-modal-close" aria-label="Close" onClick={onClose} style={{ color: colors.text }}>
              ×
            </button>
          </div>
          <p style={{ margin: '0.5rem 0 0', color: colors.textSecondary, lineHeight: 1.5, fontWeight: 450 }}>{SHARE_NOTICE}</p>
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, overflow: 'hidden', margin: 0 }}>
          <div className="homeowner-pro-sheet-body">
            <label htmlFor="homeowner-pro-name" style={labelStyle(colors)}>Name</label>
            <input id="homeowner-pro-name" value={form.name} onChange={setField('name')} required style={fieldStyle} />
            <SearchableCategorySelect
              id="homeowner-pro-trade"
              label="Trade"
              value={tradeValue}
              onChange={(value) => setForm((current) => ({ ...current, trade: value }))}
              options={trades}
              emptyLabel="Choose a trade"
              required
              keepUnknownValue={false}
              startOpen={startOpen}
              colors={colors}
            />
            {form.trade && !tradeValue ? (
              <p style={{ margin: '0.35rem 0 0', color: colors.textSecondary }}>Choose a trade from the list.</p>
            ) : null}
            <label htmlFor="homeowner-pro-phone" style={labelStyle(colors)}>Phone</label>
            <input id="homeowner-pro-phone" type="tel" value={form.phone} onChange={setField('phone')} style={fieldStyle} />
            <label htmlFor="homeowner-pro-email" style={labelStyle(colors)}>Email</label>
            <input id="homeowner-pro-email" type="email" value={form.email} onChange={setField('email')} style={fieldStyle} />
            <label htmlFor="homeowner-pro-website" style={labelStyle(colors)}>Website</label>
            <input id="homeowner-pro-website" type="text" inputMode="url" value={form.website} onChange={setField('website')} style={fieldStyle} />
            <label htmlFor="homeowner-pro-notes" style={labelStyle(colors)}>Notes</label>
            <textarea id="homeowner-pro-notes" value={form.notes} onChange={setField('notes')} style={{ ...fieldStyle, minHeight: '6rem' }} />
            {error ? <p style={{ color: '#b91c1c', margin: '0.75rem 0 0' }}>{error}</p> : null}
          </div>
          <div className="app-modal-actions" style={{ background: colors.surface }}>
            <button type="submit" style={{ ...actionStyle(colors, false), background: accentOr(colors), color: '#fff', border: 'none' }}>
              Save
            </button>
            <button type="button" onClick={onClose} style={actionStyle(colors, false)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </ModalLayer>
  );
}

function labelStyle(colors) {
  return {
    display: 'block',
    margin: '0.85rem 0 0.3rem',
    fontSize: '0.85rem',
    fontWeight: 650,
    color: colors.textSecondary,
  };
}

function accentOr(colors) {
  return colors.primary || '#0f766e';
}

export function ClientProsList({ pros, colors, accent, adoptedIds, onAdopt }) {
  const [expandedId, setExpandedId] = useState(null);
  const list = Array.isArray(pros) ? pros : [];
  if (list.length === 0) {
    return <p style={{ margin: 0, color: colors.textSecondary }}>{EMPTY_LIST_VENDORS}</p>;
  }
  return (
    <div style={gridStyle}>
      {list.map((pro) => {
        const expanded = expandedId === pro.id;
        const adopted = adoptedIds && adoptedIds.has(pro.id);
        return (
          <div key={pro.id} style={vendorSoftCardStyle(colors)}>
            <VendorTapCard
              vendor={{ id: pro.id, name: pro.name, category: pro.trade }}
              expanded={expanded}
              onToggle={() => setExpandedId(expanded ? null : pro.id)}
              accent={accent || colors.primary}
              contact={pro.phone ? (
                <a href={`tel:${pro.phone}`} aria-label={`Call ${pro.name}`} style={callControlStyle(accent || colors.primary, colors.border)}>
                  Call
                </a>
              ) : null}
            >
              <ServesLine record={pro} />
              {pro.phone ? <p style={{ margin: '0 0 0.75rem', color: colors.text }}>{formatPhoneDisplay(pro.phone) || pro.phone}</p> : null}
              {pro.email ? (
                <p style={{ margin: '0 0 0.75rem' }}>
                  <a href={`mailto:${pro.email}`} style={{ color: accent || colors.primary, overflowWrap: 'anywhere' }}>{pro.email}</a>
                </p>
              ) : null}
              {pro.website ? (
                <p style={{ margin: '0 0 0.75rem' }}>
                  <a href={websiteHref(pro.website)} target="_blank" rel="noopener noreferrer" style={{ color: accent || colors.primary, overflowWrap: 'anywhere' }}>
                    {pro.website}
                  </a>
                </p>
              ) : null}
            </VendorTapCard>
            {onAdopt ? (
              adopted ? (
                <p style={{ margin: '0.75rem 0 0', fontWeight: 650, color: colors.textSecondary }}>{IN_YOUR_VENDORS}</p>
              ) : (
                <button type="button" onClick={() => onAdopt(pro)} style={{ ...actionStyle(colors, false), marginTop: '0.75rem' }}>
                  {ADOPT_BUTTON}
                </button>
              )
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function ClientProsSection({ pros, colors, accent, adoptedIds, onAdopt }) {
  return (
    <section aria-label={ADDED_BY_CLIENTS} style={{ minWidth: 0 }}>
      <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: colors.text }}>{ADDED_BY_CLIENTS}</h3>
      <p style={{ margin: '0 0 1rem', color: colors.textSecondary, lineHeight: 1.45 }}>{ADDED_BY_CLIENTS_LINE}</p>
      <ClientProsList
        pros={pros}
        colors={colors}
        accent={accent}
        adoptedIds={adoptedIds}
        onAdopt={onAdopt}
      />
    </section>
  );
}

export function AdoptNotice() {
  return (
    <p style={{ margin: '1rem 0 0', lineHeight: 1.45 }}>{ADOPT_NOTICE}</p>
  );
}

export { CLIENT_PROS_HEADING, ADD_VENDOR };
